import 'bootstrap/dist/css/bootstrap.min.css';
import React, { useState } from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useWebStore } from './context/WebStoreContext';
import { isWebOrStoreRoute, resetSystemTheme, applyEcommerceTheme } from './utils/ecommerceHelper';
import { WebStoreProvider } from './context/WebStoreContext';
import Login from './components/Login';
import Home from './components/Home';
import NotFound from './components/NotFound';
import AddressBook from './components/AddressBook';
import Layout from './components/Layout';
import Quoter from './components/Quoter';
import QuoteViewer from './components/QuoteViewer';
import EcommerceStore from './components/ecommerce/EcommerceStore';
import EcommerceAdminSettings from './components/ecommerce/EcommerceAdminSettings';
import LandingPage from './components/landing/LandingPage';
import EmpresaPage from './components/landing/EmpresaPage';
import DondeEstamosPage from './components/landing/DondeEstamosPage';
import ContactoPage from './components/landing/ContactoPage';
import FaqPage from './components/landing/FaqPage';
import NovedadesPage from './components/landing/NovedadesPage';
import DownloadsPage from './components/landing/DownloadsPage';
import LandingAdminSettings from './components/landing/LandingAdminSettings';
import WebUnifiedAdminSettings from './components/landing/WebUnifiedAdminSettings';
import StoreClientsAdmin from './components/ecommerce/StoreClientsAdmin';
import ArticleImagesAdmin from './components/ArticleImagesAdmin';
import RolesProfilesAdmin from './components/RolesProfilesAdmin';
import Swal from 'sweetalert2';
import { loadRecaptchaScript } from './utils/recaptcha';

// Tiempo de inactividad por seguridad (10 minutos = 600000 ms)
const SESSION_TIMEOUT = 10 * 60 * 1000;

// Sincronizador de temas según la ruta para aislar 100% el sistema de los colores de la tienda
const RouteThemeSynchronizer = () => {
  const location = useLocation();
  const { storeConfig, landingConfig } = useWebStore();

  React.useEffect(() => {
    if (isWebOrStoreRoute(location.pathname)) {
      const colorsToApply = landingConfig?.colors || landingConfig?.styles || storeConfig?.colors;
      if (colorsToApply) {
        applyEcommerceTheme(colorsToApply, { pathname: location.pathname });
      }
    } else {
      resetSystemTheme();
    }
  }, [location.pathname, landingConfig?.colors, landingConfig?.styles, storeConfig?.colors]);

  return null;
};

const parseBool = (val) => {
  if (Array.isArray(val)) {
    if (val.length === 0) return false;
    val = val[0];
  }
  if (val === true || val === 1 || val === -1 || val === '1' || val === '-1' || val === 'true' || val === 't' || val === 'v' || val === 'verdadero' || val === 'si' || val === 'sí') return true;
  return false;
};

const isCompanyWebEnabled = (u) => {
  const active = u?.empresa_activa;
  if (active?.l_sitien !== undefined) return parseBool(active.l_sitien);
  const matched = u?.empresas?.find(
    e => String(e.c_nuges ?? e.id) === String(active?.c_nuges ?? active?.id)
  );
  if (matched?.l_sitien !== undefined) return parseBool(matched.l_sitien);
  return false;
};

function App() {
  // Inicializar Google reCAPTCHA v3 en toda la aplicación
  React.useEffect(() => {
    loadRecaptchaScript();
  }, []);

  // Estado de autenticación
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem('isLogged') === 'true';
  });


  // Recuperamos el usuario desde localStorage para que no se pierda al recargar la página
  // Recuperamos el usuario priorizando sessionStorage (aislado por pestaña) y luego localStorage
  const [user, setUser] = useState(() => {
    const tabSavedUser = sessionStorage.getItem('tabUserData');
    const savedUser = tabSavedUser || localStorage.getItem('userData');
    if (!savedUser) return null;
    try {
      const parsed = JSON.parse(savedUser);
      const tabCompId = sessionStorage.getItem('tabCompanyId');
      if (tabCompId && Array.isArray(parsed?.empresas)) {
        const matched = parsed.empresas.find(
          e => String(e.c_nuges || e.id) === String(tabCompId)
        );
        if (matched) {
          parsed.empresa_activa = matched;
        }
      }
      const activeId = parsed?.empresa_activa?.c_nuges || parsed?.empresa_activa?.id;
      if (activeId) {
        window.__ACTIVE_COMPANY_ID__ = String(activeId);
        sessionStorage.setItem('tabCompanyId', String(activeId));
        sessionStorage.setItem('tabUserData', JSON.stringify(parsed));
      }
      return parsed;
    } catch {
      return null;
    }
  });


  // Mantener sincronizada la empresa activa de ESTA pestaña en memoria y sessionStorage
  React.useEffect(() => {
    const activeId = user?.empresa_activa?.c_nuges || user?.empresa_activa?.id;
    if (activeId) {
      window.__ACTIVE_COMPANY_ID__ = String(activeId);
      sessionStorage.setItem('tabCompanyId', String(activeId));
      sessionStorage.setItem('tabUserData', JSON.stringify(user));
    }
  }, [user]);

  // Rehidratar siempre la lista fresca de empresas y sus flags desde /api/empresas/
  React.useEffect(() => {
    if (!isAuthenticated) return;
    axios.get('/api/empresas/').then(res => {
      if (Array.isArray(res.data) && res.data.length > 0) {
        setUser(prev => {
          if (!prev) return prev;
          const freshEmpresas = res.data;
          const currentActiveId = prev.empresa_activa?.c_nuges || prev.empresa_activa?.id;
          const freshActive = freshEmpresas.find(e => String(e.c_nuges || e.id) === String(currentActiveId)) || prev.empresa_activa;
          const updated = {
            ...prev,
            empresas: freshEmpresas,
            empresa_activa: freshActive
          };
          try {
            sessionStorage.setItem('tabUserData', JSON.stringify(updated));
            localStorage.setItem('userData', JSON.stringify(updated));
          } catch {}
          return updated;
        });
      }
    }).catch(() => {});
  }, [isAuthenticated]);




  React.useEffect(() => {
    if (!isAuthenticated) return;

    // Inicializar o verificar actividad en carga de la app
    const savedActivity = localStorage.getItem('lastActivity');
    if (!savedActivity) {
      localStorage.setItem('lastActivity', Date.now().toString());
    } else {
      const lastActivityTime = parseInt(savedActivity, 10);
      if (Date.now() - lastActivityTime > SESSION_TIMEOUT) {
        Swal.fire({
          title: 'Sesión Expirada',
          text: 'Su sesión ha expirado por inactividad.',
          icon: 'warning',
          confirmButtonColor: '#3085d6',
          confirmButtonText: 'Aceptar'
        }).then(() => {
          logoutSuccess();
        });
      
        return;
      }
    }

    let lastWriteTime = Date.now();

    const updateActivity = () => {
      const now = Date.now();
      // Limitar escrituras en localStorage para no degradar el rendimiento
      if (now - lastWriteTime > 10000) {
        localStorage.setItem('lastActivity', now.toString());
        lastWriteTime = now;
      }
    };

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });
  

    // Controlar la expiración periódicamente
    const checkInterval = setInterval(() => {
      const lastActivityTime = parseInt(localStorage.getItem('lastActivity'), 10) || Date.now();
      if (Date.now() - lastActivityTime > SESSION_TIMEOUT) {
        Swal.fire({
          title: 'Sesión Expirada',
          text: 'Su sesión ha expirado por inactividad.',
          icon: 'warning',
          confirmButtonColor: '#3085d6',
          confirmButtonText: 'Aceptar'
        }).then(() => {
          logoutSuccess();
        });
      
      }
    }, 15000);

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
    
      clearInterval(checkInterval);
    };
  }, [isAuthenticated]);

  const loginSuccess = (responseData, usernameFallback, selectedCompanyId = null) => {
    localStorage.setItem('isLogged', 'true');
    
    // El nuevo backend devuelve user en la raíz
    const backendUser = responseData?.user || {};
    const empresasList = responseData?.empresas || [];
    
    // Recuperar la empresa seleccionada explícitamente o la última guardada
    const targetId = selectedCompanyId || localStorage.getItem('lastCompanyId');
    let empresaDefecto = null;
    if (targetId) {
      empresaDefecto = empresasList.find(e => String(e.c_nuges ?? e.id) === String(targetId));
    }
    if (!empresaDefecto) {
      empresaDefecto = empresasList.find(e => String(e.c_nuges ?? e.id) === '5') || (empresasList.length > 0 ? empresasList[0] : null);
    }
    if (empresaDefecto) {
      localStorage.setItem('lastCompanyId', String(empresaDefecto.c_nuges ?? empresaDefecto.id));
    }

    const sectorName = String(backendUser.sector || '').trim().toLowerCase();
    const isSupervisor = sectorName.includes('superv') || sectorName.includes('gerenc') || sectorName.includes('sistemas');
    const isAdmin = Boolean(backendUser.l_admin || sectorName === 'admin' || sectorName === 'administrador');
    const computedRol = backendUser.rol || (isAdmin ? "Administrador" : (isSupervisor ? "Supervisor" : "Vendedor"));
    
    const userObj = {
      c_usuar: backendUser.c_usuar || usernameFallback || "",
      nombre: backendUser.nombre || backendUser.c_usuar || usernameFallback || "Usuario",
      apellido: backendUser.apellido || "",
      rol: computedRol,
      rol_id: backendUser.rol_id,
      rol_color: backendUser.rol_color,
      l_admin: Boolean(backendUser.l_admin),
      l_enwhat: Boolean(backendUser.l_enwhat),
      l_modpr: Boolean(backendUser.l_modpr),
      l_campr: Boolean(backendUser.l_campr),
      l_ancom: Boolean(backendUser.l_ancom),
      l_emincr: Boolean(backendUser.l_emincr),
      modulos_permitidos: Array.isArray(backendUser.modulos_permitidos) ? backendUser.modulos_permitidos : null,
      permisos_especiales: backendUser.permisos_especiales || null,
      sector: backendUser.sector,
      c_vende: backendUser.c_vende,
      empresa_activa: empresaDefecto,
      empresas: empresasList,
      token: responseData?.token
    };
    
    const activeId = empresaDefecto ? String(empresaDefecto.c_nuges || empresaDefecto.id) : '';
    if (activeId) {
      window.__ACTIVE_COMPANY_ID__ = activeId;
      sessionStorage.setItem('tabCompanyId', activeId);
    }
    sessionStorage.setItem('tabUserData', JSON.stringify(userObj));
    localStorage.setItem('userData', JSON.stringify(userObj));
    localStorage.setItem('lastActivity', Date.now().toString());
    setUser(userObj);
    setIsAuthenticated(true);
  };

  const changeCompany = (companyId) => {
    if (user && user.empresas) {
      const targetStr = String(companyId);
      const selectedCompany = user.empresas.find(emp => 
        String(emp.c_nuges ?? emp.id) === targetStr
      );
      if (selectedCompany) {
        const cid = String(selectedCompany.c_nuges ?? selectedCompany.id ?? companyId);
        localStorage.setItem('lastCompanyId', cid);
        localStorage.removeItem('sigecom_cached_store_config');
        localStorage.removeItem('sigecom_cached_landing_config');
        localStorage.removeItem('sigecom_cached_rubros');
        const updatedUser = { ...user, empresa_activa: selectedCompany };
        window.__ACTIVE_COMPANY_ID__ = cid;
        sessionStorage.setItem('tabCompanyId', cid);
        sessionStorage.setItem('previewCompanyId', cid);
        sessionStorage.setItem('tabUserData', JSON.stringify(updatedUser));
        localStorage.setItem('userData', JSON.stringify(updatedUser));
        setUser(updatedUser);
      }
    }
  };

  const logoutSuccess = () => {
    localStorage.removeItem('isLogged');
    localStorage.removeItem('userData');
    localStorage.removeItem('lastActivity');
    sessionStorage.removeItem('tabUserData');
    sessionStorage.removeItem('tabCompanyId');
    sessionStorage.removeItem('previewCompanyId');
    sessionStorage.removeItem('companySelectedInSession');
    window.__ACTIVE_COMPANY_ID__ = null;
    setUser(null);
    setIsAuthenticated(false);
  };

  return (
    <Router>
      <WebStoreProvider user={user}>
        <RouteThemeSynchronizer />
        <Routes>
        {/* Login: Si ya está autenticado, lo manda al Home */}
        <Route 
          path="/login" 
          element={!isAuthenticated ? <Login onLogin={loginSuccess} /> : <Navigate to="/home" />} 
        />
        
        {/* Home con Layout */}
        <Route 
          path="/home" 
          element={
            isAuthenticated ? (
              <Layout user={user} onLogout={logoutSuccess} onChangeCompany={changeCompany}>
                <Home user={user} />
              </Layout>
            ) : <Navigate to="/login" />
          } 
        />

        {/* Agenda de contactos con Layout */}
        <Route 
          path="/addressbook" 
          element={
            isAuthenticated ? (
              ((Array.isArray(user?.modulos_permitidos) && user.modulos_permitidos.length > 0)
                ? user.modulos_permitidos.includes('addressbook')
                : true) ? (
                <Layout user={user} onLogout={logoutSuccess} onChangeCompany={changeCompany}>
                  <AddressBook user={user} />
                </Layout>
              ) : (
                <Navigate to="/home" />
              )
            ) : <Navigate to="/login" />
          } 
        />

        {/* Cotizador con Layout */}
        <Route 
          path="/quoter" 
          element={
            isAuthenticated ? (
              <Layout user={user} onLogout={logoutSuccess} onChangeCompany={changeCompany}>
                <Quoter user={user} />
              </Layout>
            ) : <Navigate to="/login" />
          } 
        />

        {/* Visualizador de Presupuestos con Layout */}
        <Route 
          path="/quote-viewer" 
          element={
            isAuthenticated ? (
              <Layout user={user} onLogout={logoutSuccess} onChangeCompany={changeCompany}>
                <QuoteViewer user={user} />
              </Layout>
            ) : <Navigate to="/login" />
          } 
        />

        {/* MÓDULO 1: Configuración de Tienda Online (Admin y Supervisores) */}
        <Route 
          path="/configuracion-tienda" 
          element={
            isAuthenticated && isCompanyWebEnabled(user) ? (
              (user?.l_admin || 
               user?.rol === 'Admin' || 
               user?.rol === 'Supervisor' || 
               user?.rol === 'Supervisores' || 
               (user?.sector && (
                 String(user.sector).trim().toLowerCase() === 'admin' || 
                 String(user.sector).trim().toLowerCase() === 'administrador' || 
                 String(user.sector).toLowerCase().includes('superv') || 
                 String(user.sector).toLowerCase().includes('gerenc') || 
                 String(user.sector).toLowerCase().includes('sistemas')
               ))) ? (
                <Layout user={user} onLogout={logoutSuccess} onChangeCompany={changeCompany}>
                  <EcommerceAdminSettings user={user} />
                </Layout>
              ) : (
                <Navigate to="/home" />
              )
            ) : <Navigate to="/login" />
          } 
        />
        <Route path="/admin/configuracion-tienda" element={<Navigate to="/configuracion-tienda" replace />} />
        <Route path="/admin-tienda" element={<Navigate to="/configuracion-tienda" replace />} />
        <Route path="/admin/tienda" element={<Navigate to="/configuracion-tienda" replace />} />

        {/* MÓDULO 2: Configuración de Página Web / Home (Admin y Supervisores) */}
        <Route 
          path="/configuracion-pagina" 
          element={
            isAuthenticated && isCompanyWebEnabled(user) ? (
              (user?.l_admin || 
               user?.rol === 'Admin' || 
               user?.rol === 'Supervisor' || 
               user?.rol === 'Supervisores' || 
               (user?.sector && (
                 String(user.sector).trim().toLowerCase() === 'admin' || 
                 String(user.sector).trim().toLowerCase() === 'administrador' || 
                 String(user.sector).toLowerCase().includes('superv') || 
                 String(user.sector).toLowerCase().includes('gerenc') || 
                 String(user.sector).toLowerCase().includes('sistemas')
               ))) ? (
                <Layout user={user} onLogout={logoutSuccess} onChangeCompany={changeCompany}>
                  <LandingAdminSettings user={user} />
                </Layout>
              ) : (
                <Navigate to="/home" />
              )
            ) : <Navigate to="/login" />
          } 
        />

        {/* MÓDULO 3: Configuración Web Unificada (Página + Tienda) (Admin y Supervisores) */}
        <Route 
          path="/configuracion-web" 
          element={
            isAuthenticated && isCompanyWebEnabled(user) ? (
              ((Array.isArray(user?.modulos_permitidos) && user.modulos_permitidos.length > 0)
                ? user.modulos_permitidos.includes('configuracion-web')
                : (user?.l_admin || 
                   user?.rol === 'Admin' || 
                   user?.rol === 'Administrador' || 
                   user?.rol === 'Supervisor' || 
                   user?.rol === 'Supervisores' || 
                   (user?.sector && (
                     String(user.sector).trim().toLowerCase() === 'admin' || 
                     String(user.sector).trim().toLowerCase() === 'administrador' || 
                     String(user.sector).toLowerCase().includes('superv') || 
                     String(user.sector).toLowerCase().includes('gerenc') || 
                     String(user.sector).toLowerCase().includes('sistemas')
                   )))) ? (
                <Layout user={user} onLogout={logoutSuccess} onChangeCompany={changeCompany}>
                  <WebUnifiedAdminSettings user={user} />
                </Layout>
              ) : (
                <Navigate to="/home" />
              )
            ) : <Navigate to="/login" />
          } 
        />

        {/* MÓDULO 4: Clientes Tienda Online (Admin y Supervisores) */}
        <Route 
          path="/clientes-tienda" 
          element={
            isAuthenticated && isCompanyWebEnabled(user) ? (
              ((Array.isArray(user?.modulos_permitidos) && user.modulos_permitidos.length > 0)
                ? user.modulos_permitidos.includes('clientes-tienda')
                : (user?.l_admin || 
                   user?.rol === 'Admin' || 
                   user?.rol === 'Administrador' || 
                   user?.rol === 'Supervisor' || 
                   user?.rol === 'Supervisores' || 
                   (user?.sector && (
                     String(user.sector).trim().toLowerCase() === 'admin' || 
                     String(user.sector).trim().toLowerCase() === 'administrador' || 
                     String(user.sector).toLowerCase().includes('superv') || 
                     String(user.sector).toLowerCase().includes('gerenc') || 
                     String(user.sector).toLowerCase().includes('sistemas')
                   )))) ? (
                <Layout user={user} onLogout={logoutSuccess} onChangeCompany={changeCompany}>
                  <StoreClientsAdmin user={user} />
                </Layout>
              ) : (
                <Navigate to="/home" />
              )
            ) : <Navigate to="/login" />
          } 
        />

        {/* MÓDULO 5: Administración de Imágenes de Artículos (Admin y Supervisores) */}
        <Route 
          path="/imagenes-articulos" 
          element={
            isAuthenticated ? (
              ((Array.isArray(user?.modulos_permitidos) && user.modulos_permitidos.length > 0)
                ? user.modulos_permitidos.includes('imagenes-articulos')
                : (user?.l_admin || 
                   user?.rol === 'Admin' || 
                   user?.rol === 'Administrador' || 
                   user?.rol === 'Supervisor' || 
                   user?.rol === 'Supervisores' || 
                   (user?.sector && (
                     String(user.sector).trim().toLowerCase() === 'admin' || 
                     String(user.sector).trim().toLowerCase() === 'administrador' || 
                     String(user.sector).toLowerCase().includes('superv') || 
                     String(user.sector).toLowerCase().includes('gerenc') || 
                     String(user.sector).toLowerCase().includes('sistemas')
                   )))) ? (
                <Layout user={user} onLogout={logoutSuccess} onChangeCompany={changeCompany}>
                  <ArticleImagesAdmin user={user} />
                </Layout>
              ) : (
                <Navigate to="/home" />
              )
            ) : <Navigate to="/login" />
          } 
        />
        <Route path="/admin-imagenes" element={<Navigate to="/imagenes-articulos" replace />} />

        {/* MÓDULO 6: Administración de Roles, Perfiles y Permisos */}
        <Route 
          path="/roles-perfiles" 
          element={
            isAuthenticated ? (
              ((Array.isArray(user?.modulos_permitidos) && user.modulos_permitidos.length > 0)
                ? user.modulos_permitidos.includes('roles-perfiles')
                : (user?.l_admin || 
                   user?.rol === 'Admin' || 
                   user?.rol === 'Administrador' || 
                   user?.rol === 'Supervisor' || 
                   user?.rol === 'Supervisores' || 
                   (user?.sector && (
                     String(user.sector).trim().toLowerCase() === 'admin' || 
                     String(user.sector).trim().toLowerCase() === 'administrador' || 
                     String(user.sector).toLowerCase().includes('superv') || 
                     String(user.sector).toLowerCase().includes('gerenc') || 
                     String(user.sector).toLowerCase().includes('sistemas')
                   )))) ? (
                <Layout user={user} onLogout={logoutSuccess} onChangeCompany={changeCompany}>
                  <RolesProfilesAdmin user={user} />
                </Layout>
              ) : (
                <Navigate to="/home" />
              )
            ) : <Navigate to="/login" />
          } 
        />

        {/* Portada Principal / Landing Institucional de Hiperpel (Público) */}
        <Route 
          path="/" 
          element={<LandingPage user={user} onLogout={logoutSuccess} />} 
        />
        <Route 
          path="/home-web" 
          element={
            isCompanyWebEnabled(user) ? (
              <LandingPage user={user} onLogout={logoutSuccess} />
            ) : (
              <Navigate to="/home" />
            )
          } 
        />
        <Route 
          path="/web" 
          element={<LandingPage user={user} onLogout={logoutSuccess} />} 
        />

        {/* Tienda Online / Catálogo Ecommerce (Público) */}
        <Route 
          path="/tienda" 
          element={<EcommerceStore user={user} onLogout={logoutSuccess} />} 
        />
        <Route path="/shop" element={<Navigate to="/tienda" replace />} />
        <Route path="/catalogo" element={<Navigate to="/tienda" replace />} />

        {/* Novedades & Destacados (Público) */}
        <Route 
          path="/novedades" 
          element={<NovedadesPage user={user} onLogout={logoutSuccess} />} 
        />

        {/* Descargas & Listas de Precios (Público o Protegido según config) */}
        <Route 
          path="/descargas" 
          element={<DownloadsPage user={user} onLogout={logoutSuccess} />} 
        />
        <Route path="/listas-de-precios" element={<Navigate to="/descargas" replace />} />
        <Route path="/catalogos" element={<Navigate to="/descargas" replace />} />

        {/* Empresa / Quiénes Somos (Público) */}
        <Route 
          path="/empresa" 
          element={<EmpresaPage user={user} onLogout={logoutSuccess} />} 
        />
        <Route path="/sobre-nosotros" element={<Navigate to="/empresa" replace />} />
        <Route path="/nosotros" element={<Navigate to="/empresa" replace />} />

        {/* Dónde Estamos / Ubicación (Público) */}
        <Route 
          path="/donde-estamos" 
          element={<DondeEstamosPage user={user} onLogout={logoutSuccess} />} 
        />
        <Route path="/ubicacion" element={<Navigate to="/donde-estamos" replace />} />

        {/* Contáctenos / Atención Comercial (Público) */}
        <Route 
          path="/contacto" 
          element={<ContactoPage user={user} onLogout={logoutSuccess} />} 
        />
        <Route path="/contactenos" element={<Navigate to="/contacto" replace />} />

        {/* Preguntas Frecuentes / Centro de Ayuda (Público) */}
        <Route 
          path="/preguntas-frecuentes" 
          element={<FaqPage user={user} onLogout={logoutSuccess} />} 
        />
        <Route path="/faq" element={<Navigate to="/preguntas-frecuentes" replace />} />
        <Route path="/ayuda" element={<Navigate to="/preguntas-frecuentes" replace />} />

        {/* Ruta 404 */}
        <Route path="*" element={<NotFound user={user} />} />
      </Routes>
    </WebStoreProvider>
  </Router>
  );
}

export default App;